<template>
  <!--
    KPI strip for a set of unit rows (see ProjectUnitDetail in UnitReport.vue).

    One box per status bucket — every unit, active only, sold/leased only — and
    each box carries its OWN recency filter, so a user can read "everything we
    have" beside "what actually moved in the last 90 days" without re-filtering
    the table underneath.

    The recency window is status-appropriate: a sold/leased row is dated by its
    sold date, anything else by its price date. A row whose relevant date is
    missing drops out of a windowed box rather than silently counting as recent.
  -->
  <div class="ukb-grid">
    <div v-for="box in boxes" :key="box.key" class="ukb-card">
      <span class="ukb-accent" :style="{ background: box.accent }"></span>

      <div class="ukb-head">
        <span class="ukb-label">{{ box.label }}</span>
        <div class="ukb-range">
          <button type="button" class="ukb-range__btn" :aria-expanded="openRange === box.key ? 'true' : 'false'"
            @click="openRange = openRange === box.key ? null : box.key">
            {{ rangeLabel(box.key) }}
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          <template v-if="openRange === box.key">
            <div class="ukb-range__overlay" @click="openRange = null"></div>
            <div class="ukb-range__menu">
              <button v-for="opt in rangeOptions" :key="opt.value" type="button"
                :class="['ukb-range__item', { 'ukb-range__item--on': ranges[box.key] === opt.value }]"
                @click="setRange(box.key, opt.value)">
                {{ opt.label }}
              </button>
            </div>
          </template>
        </div>
      </div>

      <div class="ukb-value">{{ box.units }}</div>
      <div class="ukb-sub">{{ box.sub }}</div>

      <div class="ukb-metrics">
        <div class="ukb-metric">
          <span class="ukb-metric__label">Avg Size</span>
          <span class="ukb-metric__value">{{ box.size }}</span>
        </div>
        <div class="ukb-metric">
          <span class="ukb-metric__label">Gross</span>
          <span class="ukb-metric__value">{{ box.grossPsf }}</span>
          <span class="ukb-metric__alt">{{ box.grossPrice }}</span>
        </div>
        <div class="ukb-metric">
          <span class="ukb-metric__label">Net</span>
          <span class="ukb-metric__value">{{ box.netPsf }}</span>
          <span class="ukb-metric__alt">{{ box.netPrice }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const SOLD_STATUSES = ["Sold", "Leased"];

export default {
  name: "UnitKpiBoxes",

  props: {
    // Unit rows as UnitReport builds them (field_mls_unit_size_value,
    // field_mls_list_price_value, psf, net_price, custom_net_psf, status,
    // price_date, sold_date).
    rows: {
      type: Array,
      default: () => [],
    },
    // Rental $PSF is a small monthly figure, so it keeps two decimals; sale
    // $PSF reads better rounded. Matches UnitReport's own kpiCards.
    isMarketRental: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      openRange: null,
      // Per-box window in days; 0 = no window.
      ranges: { total: 0, active: 0, sold: 0 },
      rangeOptions: [
        { value: 0, label: "All time" },
        { value: 90, label: "Last 90 Days" },
        { value: 180, label: "Last 180 Days" },
        { value: 365, label: "Last 365 Days" },
      ],
    };
  },

  computed: {
    soldWord() {
      return this.isMarketRental ? "Leased" : "Sold";
    },
    buckets() {
      const rows = this.rows || [];
      return {
        total: rows,
        active: rows.filter((r) => r.status === "Active"),
        sold: rows.filter((r) => SOLD_STATUSES.includes(r.status)),
      };
    },
    boxes() {
      const defs = [
        { key: "total", label: "Total Units", accent: "#12283F" },
        { key: "active", label: "Active Units", accent: "#1F9D55" },
        { key: "sold", label: this.soldWord + " Units", accent: "#E8833A" },
      ];
      return defs.map((def) => {
        const rows = this.withinRange(this.buckets[def.key], this.ranges[def.key]);
        const m = this.metrics(rows);
        return {
          ...def,
          units: m.units.toLocaleString(),
          sub: this.subFor(def.key, rows),
          size: m.size == null ? "—" : Math.round(m.size).toLocaleString() + " sqft",
          grossPsf: this.psf(m.grossPsf),
          grossPrice: this.price(m.grossPrice),
          netPsf: this.psf(m.netPsf),
          netPrice: this.price(m.netPrice),
        };
      });
    },
  },

  methods: {
    setRange(key, value) {
      this.$set(this.ranges, key, value);
      this.openRange = null;
    },

    rangeLabel(key) {
      const opt = this.rangeOptions.find((o) => o.value === this.ranges[key]);
      return opt ? opt.label : "All time";
    },

    // A sold/leased row is dated by when it sold; anything else by its last
    // price change. Returns null when the row carries no usable date.
    rowDate(row) {
      const raw = SOLD_STATUSES.includes(row.status) ? row.sold_date : row.price_date;
      if (!raw || raw === "-" || raw === "Invalid Date") return null;
      const d = new Date(raw);
      return isNaN(d.getTime()) ? null : d;
    },

    withinRange(rows, days) {
      if (!days) return rows || [];
      const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
      return (rows || []).filter((r) => {
        const d = this.rowDate(r);
        return d != null && d.getTime() >= cutoff;
      });
    },

    isNum(v) {
      return v !== null && v !== undefined && v !== "" && v !== "-" && !isNaN(Number(v));
    },

    avg(rows, fn) {
      const vals = rows.map(fn).filter(this.isNum).map(Number);
      if (!vals.length) return null;
      return vals.reduce((s, v) => s + v, 0) / vals.length;
    },

    metrics(rows) {
      return {
        units: rows.length,
        size: this.avg(rows, (r) => r.field_mls_unit_size_value),
        grossPrice: this.avg(rows, (r) => r.field_mls_list_price_value),
        grossPsf: this.avg(rows, (r) => r.psf),
        netPrice: this.avg(rows, (r) => r.net_price),
        netPsf: this.avg(rows, (r) => r.custom_net_psf),
      };
    },

    subFor(key, rows) {
      if (key === "total") {
        const sold = rows.filter((r) => SOLD_STATUSES.includes(r.status)).length;
        const pct = rows.length ? Math.round((sold / rows.length) * 100) : 0;
        return `${sold.toLocaleString()} ${this.soldWord.toLowerCase()} · ${pct}%`;
      }
      const all = this.buckets[key === "active" ? "active" : "sold"].length;
      const pct = all ? Math.round((rows.length / all) * 100) : 0;
      return this.ranges[key]
        ? `${pct}% of all ${key === "active" ? "active" : this.soldWord.toLowerCase()} units`
        : key === "active"
          ? "available inventory"
          : "confirmed transactions";
    },

    psf(n) {
      if (n == null) return "—";
      return this.isMarketRental
        ? `$${Number(n).toFixed(2)}`
        : `$${Math.round(n).toLocaleString()}`;
    },

    price(n) {
      if (n == null) return "—";
      return `$${Math.round(n).toLocaleString()}`;
    },
  },
};
</script>

<style scoped>
.ukb-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.ukb-card {
  position: relative;
  background: #fff;
  border: 1px solid #e9e8e9;
  border-radius: var(--rpd-r-md, 10px);
  padding: 14px 16px 12px 18px;
  overflow: hidden;
}

.ukb-accent {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
}

.ukb-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.ukb-label {
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #8a8a8a;
}

.ukb-range {
  position: relative;
  margin-left: auto;
}

.ukb-range__btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid #c9ccd2;
  border-radius: var(--rpd-r-sm, 6px);
  background: #fff;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 10.5px;
  color: #12283f;
  padding: 3px 7px;
  cursor: pointer;
  white-space: nowrap;
}

.ukb-range__overlay {
  position: fixed;
  inset: 0;
  z-index: 20;
}

.ukb-range__menu {
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  z-index: 21;
  min-width: 132px;
  background: #fff;
  border: 1px solid #e9e8e9;
  border-radius: var(--rpd-r-sm, 6px);
  box-shadow: var(--rpd-shadow-md, 0 8px 24px -8px rgba(14, 27, 44, .18));
  padding: 4px;
}

.ukb-range__item {
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  border-radius: 4px;
  font-size: 11.5px;
  color: #12283f;
  padding: 5px 8px;
  cursor: pointer;
}

.ukb-range__item:hover {
  background: #f7f8fb;
}

.ukb-range__item--on {
  background: #edf2fe;
  color: #343376;
  font-weight: 600;
}

.ukb-value {
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  font-size: 24px;
  line-height: 1.1;
  color: #12283f;
}

.ukb-sub {
  font-size: 11px;
  color: #8a8a8a;
  margin-top: 2px;
}

.ukb-metrics {
  display: flex;
  gap: 10px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #f0f1f4;
}

.ukb-metric {
  flex: 1 1 0;
  min-width: 0;
}

.ukb-metric__label {
  display: block;
  font-size: 10px;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #8a8a8a;
}

.ukb-metric__value {
  display: block;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 13px;
  color: #12283f;
  white-space: nowrap;
}

.ukb-metric__alt {
  display: block;
  font-size: 10.5px;
  color: #5c6b80;
  white-space: nowrap;
}

/* ── Dark mode ───────────────────────────────────────────── */
html.dark .ukb-card {
  background: #0e1b2c;
  border-color: rgba(255, 255, 255, 0.08);
}

html.dark .ukb-label,
html.dark .ukb-sub,
html.dark .ukb-metric__label {
  color: rgba(255, 255, 255, 0.45);
}

html.dark .ukb-value,
html.dark .ukb-metric__value {
  color: #d6e4f0;
}

html.dark .ukb-metric__alt {
  color: rgba(255, 255, 255, 0.55);
}

html.dark .ukb-metrics {
  border-top-color: rgba(255, 255, 255, 0.08);
}

html.dark .ukb-range__btn {
  background: #15273d;
  border-color: rgba(255, 255, 255, 0.14);
  color: #d6e4f0;
}

html.dark .ukb-range__menu {
  background: #15273d;
  border-color: rgba(255, 255, 255, 0.1);
}

html.dark .ukb-range__item {
  color: #d6e4f0;
}

html.dark .ukb-range__item:hover {
  background: rgba(255, 255, 255, 0.06);
}

html.dark .ukb-range__item--on {
  background: rgba(52, 51, 118, 0.5);
  color: #cfd6ff;
}

/* ── Responsive ──────────────────────────────────────────── */
@media (max-width: 1199.98px) {
  .ukb-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767.98px) {
  .ukb-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .ukb-metrics {
    flex-wrap: wrap;
  }

  .ukb-metric {
    flex: 1 1 45%;
  }
}
</style>
